"use client"

import React from "react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog"
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerTrigger,
} from "@/components/ui/drawer"
import { Info, Waves, Wind, MousePointer2, AlertTriangle, Navigation } from "lucide-react"
import { usePlatform, triggerHaptic } from "@/hooks/use-platform"

interface ForecastInfoPopupProps {
  children?: React.ReactNode
}

export function ForecastInfoPopup({ children }: ForecastInfoPopupProps) {
  const { isMobile } = usePlatform()
  const [open, setOpen] = React.useState(false)

  const handleOpen = (newOpen: boolean) => {
    if (newOpen) triggerHaptic('selection')
    setOpen(newOpen)
  }

  const content = (
    <div className="space-y-6 pb-8">
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-blue-500">
          <Navigation className="w-5 h-5 rotate-45" />
          <h4 className="font-black uppercase tracking-tight text-sm">Conditions Map</h4>
        </div>
        <div className="space-y-4 text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
          <div className="flex gap-3">
            <div className="w-1.5 h-auto bg-emerald-500 rounded-full shrink-0" />
            <div>
              <span className="font-bold text-slate-900 dark:text-white">Green Arrows:</span>
              <p>Show wind direction. The arrow points in the direction the wind is blowing towards. Longer arrows indicate stronger winds.</p>
            </div>
          </div>
          <div className="flex gap-3">
            <div className="w-1.5 h-auto bg-blue-600 rounded-full shrink-0" />
            <div>
              <span className="font-bold text-slate-900 dark:text-white">Blue Arrows:</span>
              <p>Show swell/wave direction. These point in the direction waves are travelling (usually towards the shore). Longer arrows indicate higher waves.</p>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center gap-2 text-orange-500">
          <AlertTriangle className="w-5 h-5" />
          <h4 className="font-black uppercase tracking-tight text-sm">Quality Indicators (Beta)</h4>
        </div>
        <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
          Our surf and kite quality ratings are currently in <span className="font-bold text-slate-900 dark:text-white underline decoration-orange-500">Beta</span>. These algorithms analyze wave height, period, wind speed, and direction relative to the spot's orientation to give you an estimate of the session quality.
        </p>
      </div>

      <div className="space-y-3">
        <div className="flex items-center gap-2 text-slate-900 dark:text-white">
          <MousePointer2 className="w-5 h-5" />
          <h4 className="font-black uppercase tracking-tight text-sm">Interactive Forecast</h4>
        </div>
        <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
          Hover or tap anywhere on the hourly charts or quality timelines to update the map and see the exact conditions for that specific time.
        </p>
      </div>
    </div>
  )

  const trigger = children || (
    <button className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors">
      <Info className="w-4 h-4 text-slate-400" />
    </button>
  )

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={handleOpen}>
        <DrawerTrigger asChild>
          {trigger}
        </DrawerTrigger>
        <DrawerContent className="px-6 bg-white dark:bg-slate-900 border-none rounded-t-[2.5rem]">
          <div className="mx-auto w-12 h-1.5 bg-slate-200 dark:bg-slate-800 rounded-full mt-4 mb-2" />
          <DrawerHeader className="px-0 mb-4">
            <DrawerTitle className="text-2xl font-black uppercase tracking-tight text-slate-900 dark:text-white text-center">
              Forecast Guide
            </DrawerTitle>
            <DrawerDescription className="sr-only">
              Learn how to read the conditions map and quality indicators
            </DrawerDescription>
          </DrawerHeader>
          {content}
        </DrawerContent>
      </Drawer>
    )
  }

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogTrigger asChild>
        {trigger}
      </DialogTrigger>
      <DialogContent className="max-w-md bg-white dark:bg-slate-900 border-none rounded-[2rem]">
        <DialogHeader>
          <DialogTitle className="text-2xl font-black uppercase tracking-tight text-slate-900 dark:text-white">
            Forecast Guide
          </DialogTitle>
          <DialogDescription className="sr-only">
            Learn how to read the conditions map and quality indicators
          </DialogDescription>
        </DialogHeader>
        {content}
      </DialogContent>
    </Dialog>
  )
}
